// src/pages/Register.jsx
import { useState } from 'react';
import { auth } from '../lib/api';

const S = {
  page: {
    minHeight: '100vh',
    background: '#080A0F',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: "'DM Sans', sans-serif",
  },
  wrap: { width: '100%', maxWidth: 460 },
  logo: { textAlign: 'center', marginBottom: 32 },
  logoText: { fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 28, letterSpacing: '-0.02em' },
  logoSub: { fontSize: 13, color: '#8A96A8', marginTop: 4 },
  stepRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 },
  card: { background: '#161D27', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 32 },
  errorBox: {
    background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.25)',
    borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#FF4757',
  },
  stepTitle: { fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 20, color: '#E8EDF5', marginBottom: 4 },
  stepSub: { fontSize: 13, color: '#8A96A8', marginBottom: 24 },
  label: {
    display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: '#8A96A8', marginBottom: 6,
  },
  input: {
    width: '100%', background: '#0D1117', border: '1px solid rgba(255,255,255,0.07)',
    color: '#E8EDF5', borderRadius: 8, padding: '11px 14px', fontSize: 14, outline: 'none',
    boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif", transition: 'border-color 0.18s',
  },
  select: {
    width: '100%', background: '#0D1117', border: '1px solid rgba(255,255,255,0.07)',
    color: '#E8EDF5', borderRadius: 8, padding: '11px 14px', fontSize: 14, outline: 'none',
    boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
  },
  btnPrimary: {
    width: '100%', background: '#00E5A0', color: '#000', fontWeight: 700, fontSize: 15,
    border: 'none', borderRadius: 8, padding: '13px 20px', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", marginTop: 8,
  },
  btnGhost: {
    flex: 1, background: 'transparent', color: '#8A96A8', fontWeight: 600, fontSize: 14,
    border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '12px 20px',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  btnRow: { display: 'flex', gap: 12, marginTop: 8 },
  fieldGroup: { marginBottom: 16 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  hint: { fontSize: 11, color: '#5A6478', marginTop: 5 },
  footer: { textAlign: 'center', fontSize: 13, color: '#8A96A8', marginTop: 24 },
  link: { color: '#00E5A0', fontWeight: 600, cursor: 'pointer' },
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
];

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AE', name: 'UAE' },
  { code: 'OTHER', name: 'Other' },
];

function Field({ label, hint, children }) {
  return (
    <div style={S.fieldGroup}>
      <label style={S.label}>{label}</label>
      {children}
      {hint && <div style={S.hint}>{hint}</div>}
    </div>
  );
}

function StepIndicator({ current }) {
  const steps = ['Account', 'Personal', 'Address'];
  return (
    <div style={S.stepRow}>
      {steps.map((label, i) => {
        const num = i + 1;
        const done = current > num;
        const active = current === num;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: done || active ? '#00E5A0' : '#1C2535',
                border: `2px solid ${done || active ? '#00E5A0' : 'rgba(255,255,255,0.07)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, color: done || active ? '#000' : '#5A6478',
              }}>
                {done ? '✓' : num}
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: active ? '#00E5A0' : done ? '#8A96A8' : '#5A6478' }}>
                {label}
              </span>
            </div>
            {i < 2 && <div style={{ width: 32, height: 1, background: current > num + 1 ? '#00E5A0' : 'rgba(255,255,255,0.07)', margin: '0 10px' }}/>}
          </div>
        );
      })}
    </div>
  );
}

function PasswordStrength({ password }) {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const colors = ['', '#FF4757', '#FFA502', '#00B37D', '#00E5A0'];
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  if (!password) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= score ? colors[score] : 'rgba(255,255,255,0.07)' }}/>
        ))}
      </div>
      <span style={{ fontSize: 11, color: colors[score], fontWeight: 600 }}>{labels[score]}</span>
    </div>
  );
}

export default function Register() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '',
    firstName: '', lastName: '', phone: '', dateOfBirth: '',
    address: '', city: '', state: '', zipCode: '', country: 'US',
    affiliateCode: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAffiliate, setShowAffiliate] = useState(false);
  const [focused, setFocused] = useState(null);

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const inp = (name, extra = {}) => ({
    ...S.input, ...extra,
    borderColor: focused === name ? '#00E5A0' : 'rgba(255,255,255,0.07)',
  });

  function validateStep1() {
    if (!form.email) return 'Email is required.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    if (form.password !== form.confirmPassword) return 'Passwords do not match.';
    let score = 0;
    if (form.password.length >= 8) score++;
    if (/[A-Z]/.test(form.password)) score++;
    if (/[0-9]/.test(form.password)) score++;
    if (score < 2) return 'Password too weak. Add uppercase letters and numbers.';
    return null;
  }

  function validateStep2() {
    if (!form.firstName || !form.lastName) return 'First and last name are required.';
    if (!form.phone) return 'Phone number is required.';
    if (!form.dateOfBirth) return 'Date of birth is required.';
    return null;
  }

  function nextStep(e) {
    e.preventDefault();
    setError('');
    const err = step === 1 ? validateStep1() : validateStep2();
    if (err) { setError(err); return; }
    setStep(s => s + 1);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.address || !form.city || !form.state || !form.zipCode) {
      setError('Please fill in all address fields.');
      return;
    }
    setLoading(true);
    try {
      await auth.register({
        name: `${form.firstName} ${form.lastName}`,
        email: form.email,
        password: form.password,
        phone: form.phone,
        dateOfBirth: form.dateOfBirth,
        address: { line1: form.address, city: form.city, state: form.state, zipCode: form.zipCode, country: form.country },
        affiliateCode: form.affiliateCode || undefined,
      });
      await auth.login({ email: form.email, password: form.password });
sessionStorage.setItem('scrollToPricing', 'true');
window.location.href = '/';
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      setLoading(false);
    }
  }

  const maxDob = new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { background: #080A0F; min-height: 100%; }
        input::placeholder { color: #5A6478; }
        select option { background: #161D27; color: #E8EDF5; }
        input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.4); cursor: pointer; }
        .cf-reg-primary:hover { background: #00ffb3 !important; transform: translateY(-1px); }
        .cf-reg-ghost:hover { border-color: rgba(255,255,255,0.2) !important; color: #E8EDF5 !important; }
        .cf-ref-btn:hover { color: #00E5A0 !important; }
      `}</style>

      <div style={S.page}>
        <div style={S.wrap}>

          <div style={S.logo}>
            <div style={S.logoText}>
              <span style={{ color: '#00E5A0' }}>Clear</span>Fund
            </div>
            <div style={S.logoSub}>Create your account</div>
          </div>

          <StepIndicator current={step} />

          <div style={S.card}>
            {error && <div style={S.errorBox}>⚠ {error}</div>}

            {/* ── STEP 1 ── */}
            {step === 1 && (
              <div>
                <div style={S.stepTitle}>Account Details</div>
                <div style={S.stepSub}>Set up your login credentials.</div>

                <Field label="Email Address">
                  <input type="email" value={form.email} onChange={update('email')}
                    placeholder="you@example.com" style={inp('email')}
                    onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} autoComplete="email"/>
                </Field>

                <Field label="Password">
                  <input type="password" value={form.password} onChange={update('password')}
                    placeholder="Min. 8 characters" style={inp('pw')}
                    onFocus={() => setFocused('pw')} onBlur={() => setFocused(null)} autoComplete="new-password"/>
                  <PasswordStrength password={form.password} />
                </Field>

                <Field label="Confirm Password">
                  <input type="password" value={form.confirmPassword} onChange={update('confirmPassword')}
                    placeholder="Repeat password"
                    style={inp('cpw', { borderColor: form.confirmPassword && form.password !== form.confirmPassword ? '#FF4757' : focused === 'cpw' ? '#00E5A0' : 'rgba(255,255,255,0.07)' })}
                    onFocus={() => setFocused('cpw')} onBlur={() => setFocused(null)} autoComplete="new-password"/>
                  {form.confirmPassword && form.password !== form.confirmPassword && (
                    <div style={{ ...S.hint, color: '#FF4757' }}>Passwords don't match</div>
                  )}
                </Field>

                <button className="cf-reg-primary" onClick={nextStep} style={S.btnPrimary}>Continue →</button>

                <div style={S.footer}>
                  Already have an account?{' '}
                  <span style={S.link} onClick={() => window.location.href = '/'}>Sign in</span>
                </div>
              </div>
            )}

            {/* ── STEP 2 ── */}
            {step === 2 && (
              <div>
                <div style={S.stepTitle}>Personal Information</div>
                <div style={S.stepSub}>Required for account verification and payouts.</div>

                <div style={S.grid2}>
                  <Field label="First Name">
                    <input type="text" value={form.firstName} onChange={update('firstName')}
                      placeholder="Alex" style={inp('fn')}
                      onFocus={() => setFocused('fn')} onBlur={() => setFocused(null)} autoComplete="given-name"/>
                  </Field>
                  <Field label="Last Name">
                    <input type="text" value={form.lastName} onChange={update('lastName')}
                      placeholder="Johnson" style={inp('ln')}
                      onFocus={() => setFocused('ln')} onBlur={() => setFocused(null)} autoComplete="family-name"/>
                  </Field>
                </div>

                <Field label="Phone Number">
                  <input type="tel" value={form.phone} onChange={update('phone')}
                    placeholder="+1 (555) 000-0000" style={inp('phone')}
                    onFocus={() => setFocused('phone')} onBlur={() => setFocused(null)} autoComplete="tel"/>
                </Field>

                <Field label="Date of Birth" hint="Must be 18 or older to trade.">
                  <input type="date" value={form.dateOfBirth} onChange={update('dateOfBirth')}
                    max={maxDob} style={inp('dob')}
                    onFocus={() => setFocused('dob')} onBlur={() => setFocused(null)}/>
                </Field>

                <div style={S.btnRow}>
                  <button className="cf-reg-ghost" onClick={() => setStep(1)} style={S.btnGhost}>← Back</button>
                  <button className="cf-reg-primary" onClick={nextStep} style={{ ...S.btnPrimary, flex: 1, marginTop: 0 }}>Continue →</button>
                </div>
              </div>
            )}

            {/* ── STEP 3 ── */}
            {step === 3 && (
              <div>
                <div style={S.stepTitle}>Home Address</div>
                <div style={S.stepSub}>Required for tax forms and payout processing.</div>

                <Field label="Country">
                  <select value={form.country} onChange={update('country')} style={S.select}>
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </Field>

                <Field label="Street Address">
                  <input type="text" value={form.address} onChange={update('address')}
                    placeholder="123 Main St" style={inp('addr')}
                    onFocus={() => setFocused('addr')} onBlur={() => setFocused(null)} autoComplete="street-address"/>
                </Field>

                <Field label="City">
                  <input type="text" value={form.city} onChange={update('city')}
                    placeholder="New York" style={inp('city')}
                    onFocus={() => setFocused('city')} onBlur={() => setFocused(null)} autoComplete="address-level2"/>
                </Field>

                <div style={S.grid2}>
                  <Field label={form.country === 'US' ? 'State' : 'Province / Region'}>
                    {form.country === 'US' ? (
                      <select value={form.state} onChange={update('state')} style={S.select}>
                        <option value="">Select state</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input type="text" value={form.state} onChange={update('state')}
                        placeholder="Region" style={inp('state')}
                        onFocus={() => setFocused('state')} onBlur={() => setFocused(null)}/>
                    )}
                  </Field>
                  <Field label={form.country === 'US' ? 'ZIP Code' : 'Postal Code'}>
                    <input type="text" value={form.zipCode} onChange={update('zipCode')}
                      placeholder={form.country === 'US' ? '10001' : 'Postal code'} style={inp('zip')}
                      onFocus={() => setFocused('zip')} onBlur={() => setFocused(null)} autoComplete="postal-code"/>
                  </Field>
                </div>

                {!showAffiliate ? (
                  <div style={{ marginBottom: 16 }}>
                    <button className="cf-ref-btn" onClick={() => setShowAffiliate(true)}
                      style={{ background: 'none', border: 'none', color: '#8A96A8', fontSize: 13, cursor: 'pointer', padding: 0 }}>
                      + Have a referral code?
                    </button>
                  </div>
                ) : (
                  <Field label="Referral / Affiliate Code (Optional)">
                    <input type="text" value={form.affiliateCode} onChange={update('affiliateCode')}
                      placeholder="XXXXXX" style={{ ...inp('aff'), textTransform: 'uppercase' }}
                      onFocus={() => setFocused('aff')} onBlur={() => setFocused(null)}/>
                  </Field>
                )}

                <div style={{ fontSize: 11, color: '#5A6478', marginBottom: 16, lineHeight: 1.7 }}>
                  By registering you agree to our{' '}
                  <a href="/terms" style={{ color: '#8A96A8' }}>Terms</a> and{' '}
                  <a href="/privacy" style={{ color: '#8A96A8' }}>Privacy Policy</a>.
                </div>

                <div style={S.btnRow}>
                  <button className="cf-reg-ghost" onClick={() => setStep(2)} style={S.btnGhost}>← Back</button>
                  <button className="cf-reg-primary" onClick={handleSubmit} disabled={loading}
                    style={{ ...S.btnPrimary, flex: 1, marginTop: 0, opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                    {loading ? 'Creating account...' : 'Create Account ✓'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
